import {useState} from 'react';
import {CheckCircle, Clock, Mail, MapPin, Phone, Plane, Send, Ship} from 'lucide-react';
import {Button} from '@/components/ui/button';
import {Input} from '@/components/ui/input';
import {Card, CardContent} from '@/components/ui/card';
import {useToast} from '@/hooks/use-toast';
import {useLanguage} from '@/contexts/LanguageContext';
import {sendContactRequest} from '@/api/contact';
import {MessengerButtons, PHONE_DISPLAY, PHONE_NUMBER} from '@/components/MessengerLinks';
import {trackContactClick, trackLead} from '@/lib/analytics';
import Reveal from '@/components/Reveal';

const EMPTY_FORM = {
    name: '',
    email: '',
    phone: '',
    deliveryType: 'sea' as 'sea' | 'air',
    cityOfDeparture: '',
    deliveryCity: '',
    company: '' // honeypot — приховане поле для ботів
};

const Contact = () => {
    const {t} = useLanguage();
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const {toast} = useToast();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            await sendContactRequest(formData);
            trackLead();
            setIsSubmitted(true);
            setFormData(EMPTY_FORM);
        } catch (error) {
            console.error('Error submitting contact form:', error);
            toast({
                title: t('contact.toast.error.title'),
                description: error instanceof Error ? error.message : 'An unexpected error occurred',
                variant: 'destructive',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Мʼяке очищення: дозволяємо міжнародні номери, прибираємо лише сміття
    const sanitizePhone = (value: string): string =>
        value.replace(/[^\d+\s()-]/g, '').slice(0, 20);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const {name, value} = e.target;
        setFormData({
            ...formData,
            [name]: name === 'phone' ? sanitizePhone(value) : value
        });
    };

    const contactInfo = [
        {
            icon: MapPin,
            title: t('contact.locations.title'),
            content: <p className="text-sm text-gray-600">{t('contact.address.office')}</p>
        },
        {
            icon: Phone,
            title: t('contact.phone.title'),
            content: (
                <div className="space-y-3">
                    <a
                        href={`tel:${PHONE_NUMBER}`}
                        onClick={() => trackContactClick('phone')}
                        className="text-primary hover:underline text-sm font-medium block"
                    >
                        {PHONE_DISPLAY}
                    </a>
                    <a
                        href={`mailto:${t('contact.email')}`}
                        onClick={() => trackContactClick('email')}
                        className="text-primary hover:underline text-sm block"
                    >
                        {t('contact.email')}
                    </a>
                    <MessengerButtons variant="icons"/>
                </div>
            )
        },
        {
            icon: Clock,
            title: t('contact.hours.title'),
            content: (
                <div className="space-y-2">
                    <p className="text-sm text-gray-600">{t('contact.hours.weekdays')}</p>
                    <p className="text-sm text-gray-600">{t('contact.hours.saturday')}</p>
                    <p className="text-sm text-gray-600">{t('contact.hours.emergency')}</p>
                </div>
            )
        }
    ];

    const deliveryTypes: {value: 'sea' | 'air'; icon: typeof Ship; label: string}[] = [
        {value: 'sea', icon: Ship, label: t('contact.form.deliveryType.sea')},
        {value: 'air', icon: Plane, label: t('contact.form.deliveryType.air')}
    ];

    return (
        <section id="contact" className="py-20 bg-gradient-to-b from-gray-50 to-white">
            <div className="container mx-auto px-4">
                {/* Section Header */}
                <Reveal>
                    <div className="text-center mb-16">
                        <div className="text-primary font-bold tracking-[0.25em] uppercase text-xs mb-3">
                            {t('nav.contact')}
                        </div>
                        <h2 className="text-4xl lg:text-5xl font-extrabold tracking-tight text-navy mb-4">
                            {t('contact.title')}
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            {t('contact.subtitle')}
                        </p>
                    </div>
                </Reveal>

                <div className="grid lg:grid-cols-3 gap-12 lg:grid-rows-1">
                    {/* Contact Form */}
                    <Reveal className="lg:col-span-2">
                        <Card className="shadow-xl border-0 h-full">
                            <CardContent className="p-8 h-full">
                                {isSubmitted ? (
                                    <div className="h-full flex flex-col items-center justify-center text-center py-12 space-y-6">
                                        <div className="bg-green-100 rounded-full p-4">
                                            <CheckCircle className="w-12 h-12 text-green-600"/>
                                        </div>
                                        <h3 className="text-3xl font-bold text-navy">{t('contact.success.title')}</h3>
                                        <p className="text-gray-600 max-w-md">{t('contact.success.text')}</p>
                                        <MessengerButtons/>
                                        <button
                                            type="button"
                                            onClick={() => setIsSubmitted(false)}
                                            className="text-primary hover:underline text-sm"
                                        >
                                            {t('contact.success.another')}
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        <h3 className="text-2xl font-bold text-navy mb-6">{t('contact.form.title')}</h3>
                                        <form onSubmit={handleSubmit} className="space-y-6">
                                            {/* Honeypot: приховане від людей, боти його заповнюють */}
                                            <input
                                                type="text"
                                                name="company"
                                                value={formData.company}
                                                onChange={handleChange}
                                                tabIndex={-1}
                                                autoComplete="off"
                                                aria-hidden="true"
                                                className="absolute -left-[9999px] top-auto h-0 w-0 opacity-0"
                                            />

                                            <div className="grid md:grid-cols-2 gap-6">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        {t('contact.form.name')} *
                                                    </label>
                                                    <Input
                                                        name="name"
                                                        value={formData.name}
                                                        onChange={handleChange}
                                                        required
                                                        className="h-12"
                                                        placeholder={t('contact.form.placeholder.name')}
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        {t('contact.form.phone')} *
                                                    </label>
                                                    <Input
                                                        name="phone"
                                                        type="tel"
                                                        value={formData.phone}
                                                        onChange={handleChange}
                                                        required
                                                        className="h-12"
                                                        placeholder={t('contact.form.placeholder.phone')}
                                                    />
                                                </div>
                                            </div>

                                            <div className="grid md:grid-cols-2 gap-6">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        {t('contact.form.cityOfDeparture')} *
                                                    </label>
                                                    <Input
                                                        name="cityOfDeparture"
                                                        value={formData.cityOfDeparture}
                                                        onChange={handleChange}
                                                        required
                                                        className="h-12"
                                                        placeholder={t('contact.form.placeholder.cityOfDeparture')}
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        {t('contact.form.deliveryCity')} *
                                                    </label>
                                                    <Input
                                                        name="deliveryCity"
                                                        value={formData.deliveryCity}
                                                        onChange={handleChange}
                                                        required
                                                        className="h-12"
                                                        placeholder={t('contact.form.placeholder.deliveryCity')}
                                                    />
                                                </div>
                                            </div>

                                            <div className="grid md:grid-cols-2 gap-6">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        {t('contact.form.deliveryType')} *
                                                    </label>
                                                    <div className="grid grid-cols-2 gap-2">
                                                        {deliveryTypes.map(({value, icon: Icon, label}) => (
                                                            <button
                                                                key={value}
                                                                type="button"
                                                                onClick={() => setFormData(prev => ({...prev, deliveryType: value}))}
                                                                className={`h-12 rounded-md border flex items-center justify-center gap-2 font-medium transition-all duration-200 ${
                                                                    formData.deliveryType === value
                                                                        ? 'border-primary bg-primary/5 text-primary ring-2 ring-primary/20'
                                                                        : 'border-gray-300 text-gray-600 hover:border-gray-400 bg-white'
                                                                }`}
                                                            >
                                                                <Icon className="w-5 h-5"/>
                                                                {label}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        {t('contact.form.email')}{' '}
                                                        <span className="text-gray-400 font-normal">({t('contact.form.email.optional')})</span>
                                                    </label>
                                                    <Input
                                                        name="email"
                                                        type="email"
                                                        value={formData.email}
                                                        onChange={handleChange}
                                                        className="h-12"
                                                        placeholder={t('contact.form.placeholder.email')}
                                                    />
                                                </div>
                                            </div>

                                            <Button
                                                type="submit"
                                                size="lg"
                                                className="w-full bg-primary hover:bg-primary/90 h-12"
                                                disabled={isSubmitting}
                                            >
                                                {isSubmitting ? (
                                                    <>
                                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                                                             xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10"
                                                                    stroke="currentColor" strokeWidth="4"></circle>
                                                            <path className="opacity-75" fill="currentColor"
                                                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                        {t('contact.form.sending')}
                                                    </>
                                                ) : (
                                                    <>
                                                        <Send className="w-5 h-5 mr-2"/>
                                                        {t('contact.form.send')}
                                                    </>
                                                )}
                                            </Button>

                                            <p className="text-xs text-gray-500 text-center">{t('contact.form.note')}</p>

                                            <div className="pt-2 border-t border-gray-100">
                                                <p className="text-sm font-medium text-gray-700 mb-3 mt-4">{t('contact.messengers.title')}</p>
                                                <MessengerButtons/>
                                            </div>
                                        </form>
                                    </>
                                )}
                            </CardContent>
                        </Card>
                    </Reveal>

                    {/* Contact Information */}
                    <div className="flex flex-col h-full justify-between space-y-4">
                        {contactInfo.map((info, index) => (
                            <Reveal key={index} delay={index * 120} className="flex-1">
                            <Card className="h-full shadow-lg border border-gray-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                                <CardContent className="p-6 h-full">
                                    <div className="flex items-start space-x-4">
                                        <div
                                            className="bg-gradient-to-br from-primary to-teal w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0">
                                            <info.icon className="w-6 h-6 text-white"/>
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-navy mb-3">{info.title}</h4>
                                            {info.content}
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                            </Reveal>
                        ))}
                    </div>
                </div>

                {/* Full Width Map */}
                <Reveal className="mt-16">
                    <div className="bg-gradient-to-b from-white to-gray-50 shadow-2xl rounded-xl overflow-hidden border border-gray-100">
                        <div className="py-6 px-8 text-center bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
                            <p className="font-bold text-navy text-2xl mb-1">{t('contact.map.title')}</p>
                            <p className="text-primary/80 font-medium">{t('contact.map.subtitle')}</p>
                        </div>
                        <div className="h-96 relative">
                            <iframe
                                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1650.1035456585983!2d35.05530426969248!3d48.462716998205096!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x40dbe2db44935a4d%3A0x9d83442c660e9ccc!2sBarykadna%20St%2C%2022A%2C%20Dnipro%2C%20Dnipropetrovs&#39;ka%20oblast%2C%2049000!5e1!3m2!1sen!2sua!4v1749884706293!5m2!1sen!2sua"
                                width="100%"
                                height="100%"
                                style={{border: 0}}
                                allowFullScreen
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                title={t('contact.locations.title')}
                            ></iframe>
                        </div>
                        <div className="py-3 px-4 bg-gradient-to-r from-primary/5 to-transparent border-t border-gray-100 flex justify-center">
                            <a
                                href="https://maps.google.com/maps?daddr=48.462717,35.055304&ll=48.462717,35.055304&z=16"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary/80 hover:text-primary text-sm font-medium flex items-center transition-colors duration-300"
                            >
                                {t('contact.map.openDirections')} <MapPin className="w-4 h-4 ml-1"/>
                            </a>
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    );
};

export default Contact;
